import { memo, useMemo, useState, useRef, useEffect } from 'react';
import { classNames } from 'primereact/utils';
import { CasesBasicItem } from '@/api';
import { SearchablePopup } from './SearchablePopup';
import { useVirtualScroll } from './useVirtualScroll';

interface CaseSelectorPopupProps {
  casesBasicList: CasesBasicItem[];
  onSelect: (caseId: string) => void;
  onClose: () => void;
  position: { top: number; left: number };
}

export const CaseSelectorPopup: React.FC<CaseSelectorPopupProps> = memo(
  ({ casesBasicList, onSelect, onClose, position }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [focusedIndex, setFocusedIndex] = useState(0);
    const isKeyboardNavRef = useRef(false);

    const filteredCases = useMemo(() => {
      if (!searchQuery.trim()) return casesBasicList;
      const query = searchQuery.toLowerCase();
      return casesBasicList.filter((caseItem) => caseItem.caseReference.toLowerCase().includes(query));
    }, [casesBasicList, searchQuery]);

    // Virtual scrolling configuration
    const ITEM_HEIGHT = 44; // py-2.5 = 10px top + 10px bottom + 20px line-height + 4px spacing

    const { virtualItems, totalHeight, scrollRef, onScroll } = useVirtualScroll({
      itemCount: filteredCases.length,
      itemHeight: ITEM_HEIGHT,
      containerHeight: 400, // maxHeight of SearchablePopup
      overscan: 5,
    });

    // Auto-scroll focused item into view when keyboard navigating
    useEffect(() => {
      if (isKeyboardNavRef.current && scrollRef.current) {
        const itemTop = focusedIndex * ITEM_HEIGHT;
        const itemBottom = itemTop + ITEM_HEIGHT;
        const scrollTop = scrollRef.current.scrollTop;
        const visibleHeight = scrollRef.current.clientHeight;
        const scrollBottom = scrollTop + visibleHeight;

        // Scroll up if item is above viewport
        if (itemTop < scrollTop) {
          scrollRef.current.scrollTop = itemTop;
        }
        // Scroll down if item is below viewport
        else if (itemBottom > scrollBottom) {
          scrollRef.current.scrollTop = itemBottom - visibleHeight;
        }

        isKeyboardNavRef.current = false;
      }
    }, [focusedIndex, ITEM_HEIGHT]);

    const handleEnterPress = () => {
      const focusedCase = filteredCases[focusedIndex];
      if (focusedCase) {
        onSelect(focusedCase.caseId);
        onClose();
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      // Mark that keyboard navigation is happening
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        isKeyboardNavRef.current = true;
      }
    };

    return (
      <SearchablePopup
        position={position}
        onClose={onClose}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder="Search cases"
        focusedIndex={focusedIndex}
        onFocusedIndexChange={setFocusedIndex}
        itemCount={filteredCases.length}
        onEnterPress={handleEnterPress}
        onKeyDown={handleKeyDown}
        width="280px"
      >
        {/* Cases list with virtual scrolling */}
        {filteredCases.length === 0 ? (
          <div className="flex items-center justify-center p-4">
            <span className="text-fletchers-gray-175 text-[14px]">No cases found</span>
          </div>
        ) : (
          <div
            ref={scrollRef}
            className="flex min-h-0 flex-1 flex-col overflow-y-auto rounded-b-lg"
            onScroll={onScroll}
          >
            {/* Virtual scroll container */}
            <div style={{ height: `${totalHeight}px`, position: 'relative' }}>
              {virtualItems.map((virtualItem) => {
                const caseItem = filteredCases[virtualItem.index];
                return (
                  <div
                    key={caseItem.caseId}
                    className={classNames(
                      'absolute right-0 left-0 cursor-pointer px-3 py-2.5',
                      virtualItem.index === focusedIndex ? 'bg-fletchers-green-50' : 'hover:bg-fletchers-green-50',
                    )}
                    style={{ top: `${virtualItem.offsetTop}px`, height: `${ITEM_HEIGHT}px` }}
                    onClick={() => {
                      onSelect(caseItem.caseId);
                      onClose();
                    }}
                    onMouseEnter={() => setFocusedIndex(virtualItem.index)}
                  >
                    <div
                      className="overflow-hidden text-[14px] leading-[20px] overflow-ellipsis whitespace-nowrap text-black first-letter:uppercase"
                      title={caseItem.caseReference}
                    >
                      {caseItem.caseReference}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </SearchablePopup>
    );
  },
);
